import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  ArrowLeft,
  Users,
  Building2,
  Mail,
  User,
  Phone,
  MapPin,
  CreditCard,
  Eye,
  EyeOff,
  Save,
  Palette,
  Briefcase
} from 'lucide-react'
import { INDUSTRY_OPTIONS } from '../../utils/industrySchemas'
import { motion } from 'framer-motion'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const NewClient = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showPassword, setShowPassword] = useState(false)
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      subscription_plan: 'basic',
      edit_quota: 50
    }
  })
  
  // Fetch billing plans (includes custom plans)
  const { data: billingPlans, isLoading: plansLoading } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data)
  )
  
  const subscriptionPlan = watch('subscription_plan')

  // Create client mutation
  const createClientMutation = useMutation(
    (clientData) => apiService.post('/clients', clientData),
    {
      onSuccess: () => {
        toast.success('Client created successfully!')
        queryClient.invalidateQueries(['clients'])
        navigate('/admin/clients')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create client')
      }
    }
  )

  const onSubmit = (data) => {
    const clientData = {
      name: data.name,
      email: data.email,
      password: data.password,
      company_name: data.company_name,
      subscription_plan: data.subscription_plan,
      edit_quota: parseInt(data.edit_quota),
      brand_colors: data.brand_colors ? [data.brand_colors] : [],
      industry: data.industry || 'general',
    }
    
    createClientMutation.mutate(clientData)
  }

  // Build plan options from /billing/plans (already includes custom plans)
  const planOptions = billingPlans ? Object.entries(billingPlans).map(([id, plan]) => ({
    id,
    name: plan.name,
    description: `£${plan.price}/month${plan.custom ? ' · Custom' : ''}`,
    quota: plan.features.editsPerMonth,
    price: plan.price,
    type: plan.custom ? 'custom' : 'standard',
    features: [
      `${plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} sites`,
      `${plan.features.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features.editsPerMonth} edits/month`,
      plan.features.storage,
      plan.features.support
    ]
  })) : []

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
    let password = ''
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return password
  }

  return (
    <div className="pt-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/clients')}
            className="text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
              Add New Client
            </h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Create a new client account and set up their subscription
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Client Form */}
          <div className="lg:col-span-2 space-y-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* User Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-accent-100 dark:bg-accent-900/20 rounded-lg">
                      <User className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                    </div>
                    User Information
                  </CardTitle>
                  <CardDescription>
                    Basic user account information for the client
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Full Name <span className="text-error-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register('name', { required: 'Name is required' })}
                        className={cn(
                          'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
                          'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                          'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                          'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
                          'transition-all duration-200',
                          errors.name && 'border-error-500 focus:ring-error-500'
                        )}
                        placeholder="John Doe"
                      />
                      {errors.name && (
                        <p className="text-error-500 text-sm mt-1">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Email Address <span className="text-error-500">*</span>
                      </label>
                      <input
                        type="email"
                        {...register('email', { 
                          required: 'Email is required',
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: 'Invalid email address'
                          }
                        })}
                        className={cn(
                          'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
                          'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                          'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                          'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
                          'transition-all duration-200',
                          errors.email && 'border-error-500 focus:ring-error-500'
                        )}
                        placeholder="john@company.com"
                      />
                      {errors.email && (
                        <p className="text-error-500 text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Password <span className="text-error-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        {...register('password', { 
                          required: 'Password is required',
                          minLength: {
                            value: 8,
                            message: 'Password must be at least 8 characters'
                          }
                        })}
                        className={cn(
                          'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md pr-20',
                          'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                          'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                          'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
                          'transition-all duration-200',
                          errors.password && 'border-error-500 focus:ring-error-500'
                        )}
                        placeholder="Enter password"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const password = generatePassword()
                            setValue('password', password, { shouldValidate: true })
                          }}
                          className="text-xs text-accent-600 hover:text-accent-500 p-1 h-auto"
                        >
                          Generate
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                          className="text-neutral-400 hover:text-neutral-600 p-1 h-auto"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    {errors.password && (
                      <p className="text-error-500 text-sm mt-1">{errors.password.message}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Company Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                      <Building2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    Company Information
                  </CardTitle>
                  <CardDescription>
                    Business details and branding preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Company Name <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register('company_name', { required: 'Company name is required' })}
                      className={cn(
                        'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
                        'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                        'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                        'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
                        'transition-all duration-200',
                        errors.company_name && 'border-error-500 focus:ring-error-500'
                      )}
                      placeholder="Acme Corporation"
                    />
                    {errors.company_name && (
                      <p className="text-error-500 text-sm mt-1">{errors.company_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Industry <span className="text-error-500">*</span>
                    </label>
                    <select
                      {...register('industry', { required: 'Industry is required' })}
                      className={cn(
                        'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
                        'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                        'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                        'transition-all duration-200',
                        errors.industry && 'border-error-500 focus:ring-error-500'
                      )}
                    >
                      <option value="">Select an industry...</option>
                      {INDUSTRY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.icon} {opt.label}
                        </option>
                      ))}
                    </select>
                    {errors.industry && (
                      <p className="text-error-500 text-sm mt-1">{errors.industry.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Brand Color
                    </label>
                    <input
                      type="color"
                      {...register('brand_colors')}
                      className="w-full h-10 border border-neutral-300 dark:border-neutral-600 rounded-lg cursor-pointer"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Subscription Plan */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-success-100 dark:bg-success-900/20 rounded-lg">
                      <CreditCard className="h-5 w-5 text-success-600 dark:text-success-400" />
                    </div>
                    Subscription Plan
                  </CardTitle>
                  <CardDescription>
                    Choose the appropriate subscription plan for the client
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {plansLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner size="sm" text="Loading plans..." />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {planOptions.map((plan) => (
                        <label
                          key={plan.id}
                          className={cn(
                            'relative cursor-pointer rounded-lg border p-4 transition-all duration-200',
                            subscriptionPlan === plan.id
                              ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20 ring-2 ring-accent-500/20'
                              : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 hover:shadow-md'
                          )}
                        >
                          <input
                            type="radio"
                            value={plan.id}
                            {...register('subscription_plan')}
                            className="sr-only"
                          />
                          <div className="space-y-2">
                            <h3 className="font-medium text-neutral-900 dark:text-neutral-100">
                              {plan.name}
                            </h3>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                              {plan.description}
                            </p>
                            <p className="text-sm font-medium text-accent-600 dark:text-accent-400">
                              £{plan.price}/month
                            </p>
                            <p className="text-xs text-neutral-600 dark:text-neutral-400">
                              {plan.quota === 'unlimited' ? 'Unlimited' : plan.quota} edits
                            </p>
                            <ul className="text-xs text-neutral-500 dark:text-neutral-400 space-y-1">
                              {plan.features.map((feature, index) => (
                                <li key={index}>• {feature}</li>
                              ))}
                            </ul>
                          </div>
                        </label>
                      ))}
                      </div>

                      <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Monthly Edit Quota
                      </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      {...register('edit_quota', { 
                        required: 'Edit quota is required',
                        min: { value: 1, message: 'Minimum 1 edit' }
                      })}
                      className={cn(
                        'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
                        'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                        'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                        'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
                        'transition-all duration-200',
                        errors.edit_quota && 'border-error-500 focus:ring-error-500'
                      )}
                      placeholder="50"
                    />
                    {errors.edit_quota && (
                      <p className="text-error-500 text-sm mt-1">{errors.edit_quota.message}</p>
                    )}
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                          Override the default plan quota if needed
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </form>
          </div>

          {/* Preview/Actions */}
          <div className="space-y-6 sticky top-4 self-start">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  Client Preview
                </CardTitle>
                <CardDescription>
                  Review the client configuration before creating
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500 dark:text-neutral-400">Plan:</span>
                    <span className="font-medium capitalize text-neutral-900 dark:text-neutral-100">
                      {planOptions.find(p => p.id === subscriptionPlan)?.name || 'Basic'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500 dark:text-neutral-400">Plan Price:</span>
                    <span className="font-medium text-neutral-900 dark:text-neutral-100">
                      £{planOptions.find(p => p.id === subscriptionPlan)?.price || 0}/month
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500 dark:text-neutral-400">Edit Quota:</span>
                    <span className="font-medium text-neutral-900 dark:text-neutral-100">
                      {planOptions.find(p => p.id === subscriptionPlan)?.quota || 50}/month
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500 dark:text-neutral-400">Status:</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300">
                      Pending payment
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={createClientMutation.isLoading}
                className="w-full"
                size="lg"
              >
                {createClientMutation.isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Client
                  </>
                )}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/admin/clients')}
                className="w-full"
                size="lg"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default NewClient